import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaRegCalendarAlt,
} from "react-icons/fa";
import { Disigner } from "../Manager/Disigner";
import { Storage } from "../Manager/Storage";
import { Button, search, sortDate } from "../../util/main";
import { ready } from "../../util/icon";
import {
  getNewStatusFetch,
  putFinishStatusFetch,
} from "../../features/action/fetchStatus";
import { checkIsName } from "../../features/redux/authSlice";
import { useSearch } from "../../context/SearchContext";

/**
 * @param {string} str - Дата в формате DD/MM/YY или DD/MM/YYYY
 * @returns {Date|null}
 */
function parseOrderDate(str) {
  if (!str || typeof str !== "string") return null;
  const [d, m, y] = str.split("/");
  if (!d || !m || !y) return null;
  const year = y.length === 2 ? 2000 + Number(y) : Number(y);
  return new Date(year, Number(m) - 1, Number(d));
}

/**
 * @param {Date} date
 * @returns {string} YYYY-MM-DD
 */
function dayKey(date) {
  const d = String(date.getDate()).padStart(2, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${date.getFullYear()}-${m}-${d}`;
}

function dayKeyFromStr(str) {
  const d = parseOrderDate(str);
  return d ? dayKey(d) : null;
}

/**
 * @param {string} t - "HH:MM"
 * @returns {number} минуты от начала дня
 */
function toMinutes(t) {
  if (!t || typeof t !== "string") return 10 * 60;
  const [h, m] = t.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
}

function formatDateLabel(date) {
  const d = String(date.getDate()).padStart(2, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const y = String(date.getFullYear());
  return `${d}/${m}/${y}`;
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const DAY_NAMES = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

/**
 * Календарный вид страницы менеджера. Показывает заказы сгруппированные по
 * дате завершения (toDate) в виде сетки месяца. Клик по ячейке открывает
 * модалку со списком заказов за этот день, отсортированных по времени.
 *
 * @param {object} props
 * @param {Array} props.orders - массив заказов
 * @param {object} props.isName - текущий пользователь
 * @param {(id: string, order: string) => void} props.onClickEnd
 */
function CalendarView({ orders, isName, onClickEnd }) {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [selectedDate, setSelectedDate] = useState(null);

  const groupedByDay = useMemo(() => {
    const map = {};
    (orders || []).forEach((o) => {
      const k = dayKeyFromStr(o.toDate);
      if (!k) return;
      if (!map[k]) map[k] = [];
      map[k].push(o);
    });
    Object.keys(map).forEach((k) => {
      map[k].sort((a, b) => toMinutes(a.toTime) - toMinutes(b.toTime));
    });
    return map;
  }, [orders]);

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startWeekday = firstDay.getDay();

  const today = new Date();
  const todayKey = dayKey(today);

  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(new Date(year, month, d));
  }
  while (cells.length % 7 !== 0) cells.push(null);

  const monthTotal = useMemo(() => {
    let total = 0;
    for (let d = 1; d <= daysInMonth; d++) {
      const k = dayKey(new Date(year, month, d));
      total += (groupedByDay[k] || []).length;
    }
    return total;
  }, [groupedByDay, year, month, daysInMonth]);

  const handlePrev = () =>
    setCurrentMonth(new Date(year, month - 1, 1));
  const handleNext = () =>
    setCurrentMonth(new Date(year, month + 1, 1));
  const handleToday = () => {
    const d = new Date();
    setCurrentMonth(new Date(d.getFullYear(), d.getMonth(), 1));
  };

  const selectedList = selectedDate
    ? groupedByDay[dayKey(selectedDate)] || []
    : [];

  return (
    <div className="w-full">
      <div className="flex flex-col mx-auto">
        {/* -------------------------- Month header -------------------------- */}
        <div className="flex items-center justify-between my-4 font-rubik text-[#7D7D7D]">
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrev}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-white border border-[#dbdbdb] hover:bg-[#739D00] hover:text-white hover:border-[#739D00] transition-colors"
              title="Previous month"
            >
              <FaChevronLeft />
            </button>
            <button
              onClick={handleToday}
              className="h-9 px-4 rounded-lg bg-white border border-[#dbdbdb] font-rubik font-light text-[14px] text-[#7D7D7D] hover:bg-[#739D00] hover:text-white hover:border-[#739D00] transition-colors"
            >
              Today
            </button>
            <button
              onClick={handleNext}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-white border border-[#dbdbdb] hover:bg-[#739D00] hover:text-white hover:border-[#739D00] transition-colors"
              title="Next month"
            >
              <FaChevronRight />
            </button>
          </div>

          <div className="flex items-center gap-3 font-rubik">
            <FaRegCalendarAlt className="text-[#739D00]" />
            <span className="text-[clamp(18px,1.8vw,24px)] font-light tracking-wide-5">
              {MONTH_NAMES[month]} {year}
            </span>
          </div>

          <div className="font-rubik font-light text-[14px] text-[#9E9E9E]">
            {monthTotal} {monthTotal === 1 ? "order" : "orders"}
          </div>
        </div>

        {/* -------------------------- Day names row ------------------------- */}
        <div className="grid grid-cols-7 gap-2 mb-2 font-rubik text-[#9E9E9E] text-[16px] font-light text-center">
          {DAY_NAMES.map((d) => (
            <div key={d} className="py-2">
              {d}
            </div>
          ))}
        </div>

        {/* -------------------------- Month grid --------------------------- */}
        <div className="grid grid-cols-7 gap-2 mb-6">
          {cells.map((date, idx) => {
            if (!date) {
              return (
                <div
                  key={`empty-${idx}`}
                  className="h-[110px] rounded-[10px] bg-transparent"
                />
              );
            }
            const k = dayKey(date);
            const list = groupedByDay[k] || [];
            const count = list.length;
            const isToday = k === todayKey;
            const isSelected =
              selectedDate && dayKey(selectedDate) === k;
            const hasOrders = count > 0;

            return (
              <button
                key={k}
                onClick={() => setSelectedDate(date)}
                className={`relative h-[110px] rounded-[10px] border-solid border-[1px] p-2 flex flex-col items-stretch justify-between text-left transition-all hover:shadow-md hover:-translate-y-0.5 ${
                  isSelected
                    ? "border-[#739D00] bg-[#F5F8EB]"
                    : isToday
                    ? "border-[#FF6D00] bg-white"
                    : "border-[#dbdbdb] bg-white"
                }`}
              >
                <div className="flex items-start justify-between">
                  <span
                    className={`font-rubik leading-none text-[18px] ${
                      isToday
                        ? "text-[#FF6D00] font-medium"
                        : "text-[#7D7D7D] font-light"
                    }`}
                  >
                    {date.getDate()}
                  </span>
                  {hasOrders && (
                    <span className="font-rubik text-[11px] text-[#9E9E9E]">
                      {count === 1 ? "order" : "orders"}
                    </span>
                  )}
                </div>

                {hasOrders && (
                  <div className="flex items-center justify-center">
                    <span className="inline-flex items-center justify-center min-w-[48px] h-[32px] px-3 rounded-full bg-[#739D00] text-white font-rubik font-medium text-[16px] leading-none">
                      {count}
                    </span>
                  </div>
                )}

                {hasOrders ? (
                  <div className="flex gap-1 justify-center">
                    {list.slice(0, 4).map((o, i) => (
                      <span
                        key={`${o._id}-${i}`}
                        className={`w-1.5 h-1.5 rounded-full ${
                          o.place?.trim?.() === "הזמנות למחסן"
                            ? "bg-[#00599D]"
                            : "bg-[#739D00]"
                        }`}
                      />
                    ))}
                    {list.length > 4 && (
                      <span className="w-1.5 h-1.5 rounded-full bg-[#dbdbdb]" />
                    )}
                  </div>
                ) : (
                  <div className="h-1.5" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* --------------------------- Day modal ---------------------------- */}
      {selectedDate && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setSelectedDate(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl flex flex-col w-[95%] max-w-[1600px] max-h-[85vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#dbdbdb]">
              <div className="flex items-center gap-3">
                <FaRegCalendarAlt className="text-[#739D00]" />
                <p className="font-rubik text-[#7D7D7D] text-[18px] font-light tracking-wide-5">
                  {formatDateLabel(selectedDate)}
                </p>
                <span className="font-rubik text-[#9E9E9E] text-[14px] font-light">
                  · {selectedList.length}{" "}
                  {selectedList.length === 1 ? "order" : "orders"}
                </span>
              </div>
              <button
                onClick={() => setSelectedDate(null)}
                className="w-9 h-9 flex items-center justify-center rounded-full text-[#7D7D7D] hover:bg-[#f2f2f2] transition-colors"
                title="Close"
              >
                <FaTimes />
              </button>
            </div>

            <div className="overflow-y-auto p-4 bg-[#FAFAFA]">
              {selectedList.length === 0 ? (
                <p className="font-rubik text-[#9E9E9E] text-center py-16 font-light">
                  No orders on this day
                </p>
              ) : (
                selectedList.map((i) => (
                  <div
                    key={i._id}
                    className="flex flex-row items-stretch mb-1 gap-2"
                  >
                    <div className="flex items-center justify-center w-[72px] shrink-0 rounded-[10px] bg-white border border-[#dbdbdb] font-rubik text-[#739D00] font-medium text-[16px] tracking-wide-5">
                      {i.toTime || "10:00"}
                    </div>
                    <div className="flex flex-row h-[64px] flex-1 text-center rounded-[10px] border-solid border-[1px] bg-white border-[#dbdbdb]">
                      {i.place?.trim?.() !== "הזמנות למחסן" ? (
                        <Disigner i={i} isName={isName} />
                      ) : (
                        <Storage i={i} isName={isName} />
                      )}
                      {Button(ready, () => onClickEnd(i._id, i.order))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Страница менеджера в календарном стиле. Использует те же данные, что и
 * основная страница Manager, но представляет заказы сгруппированными по дате
 * завершения в виде месяца.
 */
const ManagerCal = () => {
  const { manager } = useSelector((state) => state.status);
  const isName = useSelector(checkIsName);

  const [filteredStatus, setFilteredStatus] = useState([]);
  const [confirmId, setConfirmId] = useState(null);
  const { searchQuery } = useSearch();
  const dispatch = useDispatch();

  const handleClickEnd = (id, order) => {
    setConfirmId({ id, order });
  };

  const handleConfirm = () => {
    dispatch(putFinishStatusFetch(confirmId.id));
    setConfirmId(null);
  };

  useEffect(() => {
    dispatch(getNewStatusFetch());
  }, [dispatch]);

  useEffect(() => {
    document.body.classList.add("no-scrollbar");
    document.documentElement.classList.add("no-scrollbar");
    return () => {
      document.body.classList.remove("no-scrollbar");
      document.documentElement.classList.remove("no-scrollbar");
    };
  }, []);

  useEffect(() => {
    let results = searchQuery ? search(manager, searchQuery) : manager;
    results = sortDate([...(results || [])], "toDate", false);
    setFilteredStatus(results);
  }, [searchQuery, manager]);

  return (
    <div className="w-[100%]">
      {confirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl p-6 flex flex-col items-center gap-4 min-w-[260px]">
            <p className="font-rubik text-[#7D7D7D] text-base">
              Close order{" "}
              <span className="font-semibold text-[#739D00]">
                #{confirmId.order}
              </span>
              ?
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleConfirm}
                className="px-5 py-1.5 bg-[#e6e6e6] hover:bg-[#739D00] text-[#7D7D7D] hover:text-white font-rubik rounded-lg transition-colors"
              >
                Confirm
              </button>
              <button
                onClick={() => setConfirmId(null)}
                className="px-5 py-1.5 bg-[#e6e6e6] hover:bg-[#739D00] text-[#7D7D7D] hover:text-white font-rubik rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <CalendarView
        orders={filteredStatus}
        isName={isName}
        onClickEnd={handleClickEnd}
      />
    </div>
  );
};

export default ManagerCal;
